// Libs
import { gql } from "@apollo/client";
import { apolloCore } from "lib/apollo/apollo";

import {
  dashboardItemOnProfileGqlFragment,
  toDashboardOnProfileType,
  DashboardOnProfileType,
} from "gui/profile-shared/dashboards/dashboardItemOnProfile";
import {
  queryItemOnProfileGqlFragment,
  toQueryOnProfileType,
  QueryOnProfileType,
} from "gui/profile-shared/queries/queryItemOnProfile";

import {
  StaticUserProfileDataDocument,
  StaticUserProfileDataQuery,
  StaticUserProfileDataQueryVariables,
} from "lib/types/graphql";

export default async function fetchStaticUserProfileData(
  name: string
): Promise<StaticUserProfileData | undefined> {
  const res = await apolloCore.query<
    StaticUserProfileDataQuery,
    StaticUserProfileDataQueryVariables
  >({
    query: StaticUserProfileDataDocument,
    variables: { name },
    fetchPolicy: "no-cache",
  });

  return res.data.users.map(toStaticUserProfileData)[0];
}

gql`
  query StaticUserProfileData($name: String!) {
    users(where: { name: { _eq: $name } }) {
      id
      name
      profile_image_url

      dashboards(
        where: { is_archived: { _eq: false } }
        limit: 20
        order_by: [
          { dashboard_favorite_count_all: { favorite_count: desc_nulls_last } }
          { id: asc }
        ]
      ) {
        ...DashboardItemOnProfile
      }
      dashboards_aggregate(where: { is_archived: { _eq: false } }) {
        aggregate {
          count
        }
      }

      queries(
        where: { is_temp: { _eq: false }, is_archived: { _eq: false } }
        limit: 20
        order_by: [
          { query_favorite_count_all: { favorite_count: desc_nulls_last } }
          { id: asc }
        ]
      ) {
        ...QueryItemOnProfile
      }
      queries_aggregate(
        where: { is_temp: { _eq: false }, is_archived: { _eq: false } }
      ) {
        aggregate {
          count
        }
      }
    }
  }

  ${dashboardItemOnProfileGqlFragment}
  ${queryItemOnProfileGqlFragment}
`;

export interface StaticUserProfileData {
  id: number;
  name: string;
  profile_image_url: string | null;

  dashboards: {
    list: DashboardOnProfileType[];
    total_count: number;
  };

  queries: {
    list: QueryOnProfileType[];
    total_count: number;
  };
}

function toStaticUserProfileData(
  user: StaticUserProfileDataQuery["users"][0]
): StaticUserProfileData {
  const { id, name, profile_image_url = null } = user;
  return {
    id,
    name,
    profile_image_url,

    dashboards: {
      list: user.dashboards.map(toDashboardOnProfileType),
      total_count: user.dashboards_aggregate?.aggregate?.count ?? 0,
    },

    queries: {
      list: user.queries.map(toQueryOnProfileType),
      total_count: user.queries_aggregate?.aggregate?.count ?? 0,
    },
  };
}
